import type { Category, Coupon, CouponDiscountType, Item } from '@erp/shared';
import { ArrowLeft, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import { Button, Combobox, type ComboboxOption, FormField } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { flattenCategoryOptions } from '../items/itemHelpers.js';

interface FormState {
  code: string;
  name: string;
  discountType: CouponDiscountType;
  discountValue: string;
  currencyCode: string;
  minSubtotal: string;
  maxDiscount: string;
  startsAt: string;
  endsAt: string;
  usageLimit: string;
  perCustomerLimit: string;
  productIds: string[];
  categoryIds: string[];
  isActive: boolean;
}

export function CouponFormPage({ edit = false }: { edit?: boolean }): JSX.Element {
  const { t } = useTranslation('coupons');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const tenant = useAuthStore((s) => s.tenant);

  const [items, setItems] = useState<Item[] | null>(null);
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [hydrated, setHydrated] = useState(!edit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    code: '',
    name: '',
    discountType: 'percent',
    discountValue: '',
    currencyCode: tenant?.baseCurrencyCode ?? 'USD',
    minSubtotal: '',
    maxDiscount: '',
    startsAt: '',
    endsAt: '',
    usageLimit: '',
    perCustomerLimit: '',
    productIds: [],
    categoryIds: [],
    isActive: true,
  });

  useEffect(() => {
    void api()
      .get<{ items: Item[] }>('/items')
      .then((r) => setItems(r.data.items))
      .catch(() => setItems([]));
    void api()
      .get<{ items: Category[] }>('/categories')
      .then((r) => setCategories(r.data.items))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!edit || !id) return;
    void api()
      .get<Coupon>(`/coupons/${id}`)
      .then((r) => {
        const c = r.data;
        setForm({
          code: c.code,
          name: c.name,
          discountType: c.discountType,
          discountValue: c.discountValue,
          currencyCode: c.currencyCode ?? tenant?.baseCurrencyCode ?? 'USD',
          minSubtotal: c.minSubtotal ?? '',
          maxDiscount: c.maxDiscount ?? '',
          startsAt: c.startsAt ? c.startsAt.slice(0, 10) : '',
          endsAt: c.endsAt ? c.endsAt.slice(0, 10) : '',
          usageLimit: c.usageLimit != null ? String(c.usageLimit) : '',
          perCustomerLimit: c.perCustomerLimit != null ? String(c.perCustomerLimit) : '',
          productIds: c.productIds,
          categoryIds: c.categoryIds,
          isActive: c.isActive,
        });
        setHydrated(true);
      })
      .catch(() => {
        toast.error(tc('errors:server'));
        navigate('/coupons');
      });
  }, [edit, id, navigate, tc, tenant?.baseCurrencyCode]);

  const itemOptions = useMemo<ComboboxOption[]>(
    () =>
      (items ?? [])
        .filter((it) => it.isActive)
        .map((it) => ({
          value: it.id,
          label: `${it.code} — ${it.name}`,
          keywords: `${it.code} ${it.name}`,
        })),
    [items],
  );
  const categoryOptions = useMemo(() => flattenCategoryOptions(categories ?? []), [categories]);
  const itemName = (idv: string): string => items?.find((it) => it.id === idv)?.name ?? idv;
  const categoryName = (idv: string): string =>
    categoryOptions.find((o) => o.id === idv)?.label ?? idv;

  if (!items || !categories || !hydrated) {
    return (
      <Card className="p-6">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="mt-4 h-48 w-full" />
      </Card>
    );
  }

  const setField = (patch: Partial<FormState>): void => setForm((f) => ({ ...f, ...patch }));
  const addProduct = (v?: string): void => {
    if (v && !form.productIds.includes(v)) setField({ productIds: [...form.productIds, v] });
  };
  const removeProduct = (v: string): void =>
    setField({ productIds: form.productIds.filter((x) => x !== v) });
  const addCategory = (v?: string): void => {
    if (v && !form.categoryIds.includes(v)) setField({ categoryIds: [...form.categoryIds, v] });
  };
  const removeCategory = (v: string): void =>
    setField({ categoryIds: form.categoryIds.filter((x) => x !== v) });

  const sectionTitle = (text: string): JSX.Element => (
    <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-fg-muted">{text}</h2>
  );

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim() || !form.discountValue.trim()) {
      setError(t('form.errors.required'));
      return;
    }
    if (form.discountType === 'percent') {
      const pv = Number(form.discountValue);
      if (!(pv > 0 && pv <= 100)) {
        setError(t('form.percentHint'));
        return;
      }
    }
    setError(null);
    setSaving(true);
    const payload: Record<string, unknown> = {
      code: form.code.trim(),
      name: form.name.trim(),
      discountType: form.discountType,
      discountValue: form.discountValue.trim(),
      // Sent for both types now: a fixed coupon's amount currency, and a percent
      // coupon's (optional) threshold currency. Empty ⇒ null ⇒ tenant base.
      currencyCode: form.currencyCode.trim().toUpperCase() || null,
      minSubtotal: form.minSubtotal.trim() || null,
      maxDiscount: form.discountType === 'percent' ? form.maxDiscount.trim() || null : null,
      startsAt: form.startsAt || null,
      endsAt: form.endsAt || null,
      usageLimit: form.usageLimit.trim() ? Number(form.usageLimit) : null,
      perCustomerLimit: form.perCustomerLimit.trim() ? Number(form.perCustomerLimit) : null,
      productIds: form.productIds,
      categoryIds: form.categoryIds,
      isActive: form.isActive,
    };
    try {
      if (edit && id) await api().patch(`/coupons/${id}`, payload);
      else await api().post('/coupons', payload);
      navigate('/coupons');
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'conflict' ? t('form.errors.codeTaken') : t('form.errors.server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <header className="flex items-center gap-3">
        <Link
          to="/coupons"
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-bg-subtle px-3 text-sm font-semibold text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4 rtl:scale-x-[-1]" aria-hidden />
          {tc('actions.back', { defaultValue: 'Back' })}
        </Link>
        <h1 className="text-2xl font-semibold">{edit ? t('form.editTitle') : t('form.createTitle')}</h1>
      </header>

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <Card className="p-6">
          {sectionTitle(t('form.sections.basics'))}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="c-code" label={t('form.code')} required helperText={t('form.codeHint')}>
              <Input
                id="c-code"
                value={form.code}
                onChange={(e) => setField({ code: e.target.value })}
                className="font-mono"
                maxLength={64}
              />
            </FormField>
            <FormField id="c-name" label={t('form.name')} required>
              <Input id="c-name" value={form.name} onChange={(e) => setField({ name: e.target.value })} maxLength={200} />
            </FormField>
          </div>
        </Card>

        <Card className="p-6">
          {sectionTitle(t('form.sections.discount'))}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="c-type" label={t('form.discountType')} required>
              <select
                id="c-type"
                className="h-9 w-full rounded-md border border-border bg-bg px-2 text-sm text-fg"
                value={form.discountType}
                onChange={(e) => {
                  const v = e.target.value as CouponDiscountType;
                  // maxDiscount is meaningless for a fixed coupon — clear it on switch.
                  setField(v === 'fixed' ? { discountType: v, maxDiscount: '' } : { discountType: v });
                }}
              >
                <option value="percent">{t('type.percent')}</option>
                <option value="fixed">{t('type.fixed')}</option>
              </select>
            </FormField>
            <FormField
              id="c-value"
              label={t('form.discountValue')}
              required
              helperText={form.discountType === 'percent' ? t('form.percentHint') : undefined}
            >
              <Input
                id="c-value"
                value={form.discountValue}
                onChange={(e) => setField({ discountValue: e.target.value })}
                inputMode="decimal"
                placeholder={form.discountType === 'percent' ? '10' : '0.00'}
              />
            </FormField>
            <FormField
              id="c-cur"
              label={t('form.currency')}
              required={form.discountType === 'fixed'}
              helperText={form.discountType === 'percent' ? t('form.currencyThresholdHint', { defaultValue: 'Optional — denominates the minimum spend / maximum discount. Defaults to the base currency.' }) : undefined}
            >
              <CurrencySelect
                id="c-cur"
                value={form.currencyCode}
                onChange={(c) => setField({ currencyCode: c })}
                includeEmpty={form.discountType === 'percent'}
                emptyLabel={form.discountType === 'percent' ? t('form.baseCurrencyOption', { defaultValue: 'Base currency' }) : undefined}
              />
            </FormField>
            <FormField id="c-min" label={t('form.minSubtotal')}>
              <Input
                id="c-min"
                value={form.minSubtotal}
                onChange={(e) => setField({ minSubtotal: e.target.value })}
                inputMode="decimal"
                placeholder="0.00"
              />
            </FormField>
            {form.discountType === 'percent' && (
              <FormField id="c-max" label={t('form.maxDiscount')} helperText={t('form.maxDiscountHint')}>
                <Input
                  id="c-max"
                  value={form.maxDiscount}
                  onChange={(e) => setField({ maxDiscount: e.target.value })}
                  inputMode="decimal"
                  placeholder="0.00"
                />
              </FormField>
            )}
          </div>
        </Card>

        <Card className="p-6">
          {sectionTitle(t('form.sections.validity'))}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="c-start" label={t('form.startsAt')}>
              <Input id="c-start" type="date" value={form.startsAt} onChange={(e) => setField({ startsAt: e.target.value })} />
            </FormField>
            <FormField id="c-end" label={t('form.endsAt')}>
              <Input id="c-end" type="date" value={form.endsAt} onChange={(e) => setField({ endsAt: e.target.value })} />
            </FormField>
          </div>
        </Card>

        <Card className="p-6">
          {sectionTitle(t('form.sections.limits'))}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="c-usage" label={t('form.usageLimit')} helperText={t('form.usageLimitHint')}>
              <Input
                id="c-usage"
                value={form.usageLimit}
                onChange={(e) => setField({ usageLimit: e.target.value.replace(/[^0-9]/g, '') })}
                inputMode="numeric"
              />
            </FormField>
            <FormField id="c-percust" label={t('form.perCustomerLimit')} helperText={t('form.perCustomerHint')}>
              <Input
                id="c-percust"
                value={form.perCustomerLimit}
                onChange={(e) => setField({ perCustomerLimit: e.target.value.replace(/[^0-9]/g, '') })}
                inputMode="numeric"
              />
            </FormField>
          </div>
        </Card>

        <Card className="p-6">
          {sectionTitle(t('form.sections.restrictions'))}
          <p className="mb-3 text-xs text-fg-muted">{t('form.restrictionsHint')}</p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <FormField id="c-prod" label={t('form.products')}>
                <Combobox
                  id="c-prod"
                  options={itemOptions.filter((o) => !form.productIds.includes(o.value))}
                  onChange={addProduct}
                  clearOnSelect
                  placeholder={t('form.addProduct')}
                  ariaLabel={t('form.products')}
                />
              </FormField>
              <ChipList ids={form.productIds} label={itemName} onRemove={removeProduct} removeLabel={tc('actions.remove', { defaultValue: 'Remove' })} />
            </div>
            <div>
              <FormField id="c-cat" label={t('form.categories')}>
                <Combobox
                  id="c-cat"
                  options={categoryOptions
                    .filter((o) => !form.categoryIds.includes(o.id))
                    .map((o) => ({ value: o.id, label: o.label }))}
                  onChange={addCategory}
                  clearOnSelect
                  placeholder={t('form.addCategory')}
                  ariaLabel={t('form.categories')}
                />
              </FormField>
              <ChipList ids={form.categoryIds} label={categoryName} onRemove={removeCategory} removeLabel={tc('actions.remove', { defaultValue: 'Remove' })} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          {sectionTitle(t('form.sections.options'))}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField({ isActive: e.target.checked })}
            />
            {t('form.isActive')}
          </label>
        </Card>

        {error && <p className="text-sm text-danger" role="alert">{error}</p>}

        <div className="flex gap-2">
          <Button type="submit" loading={saving}>{t('form.submit')}</Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/coupons')} disabled={saving}>
            {tc('actions.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ChipList({
  ids,
  label,
  onRemove,
  removeLabel,
}: {
  ids: string[];
  label: (id: string) => string;
  onRemove: (id: string) => void;
  removeLabel: string;
}): JSX.Element | null {
  if (ids.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {ids.map((id) => (
        <span
          key={id}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-bg-subtle px-2 py-0.5 text-xs"
        >
          {label(id)}
          <button
            type="button"
            onClick={() => onRemove(id)}
            aria-label={`${removeLabel} ${label(id)}`}
            className="text-fg-muted hover:text-danger"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </span>
      ))}
    </div>
  );
}
